import * as Api from '@ViewModels';
import React from 'react';
import {
	MultipleSelectContent,
	MultipleSelectItem,
	MultipleSelectTrigger,
	MultipleSelectValue,
} from '../../../../../../../../web/components/MultipleSelect';
import { type ComboboxItem, EditModeCombobox, EditModeMultipleSelect } from '../context';
import { getProviderConfig } from '../providers';
import { CORE_TAGS } from '../providers/constants';
import type { CustomTagSection } from '../providers/types';
import {
	getAvailableFields,
	getConfigValue,
	getFieldsDisplayValue,
	getSettingValue,
	recordToItems,
} from '../providers/utils';
import { styleSheet } from '../styles';
import { SettingGroup } from './SettingGroup';
import { SettingRow } from './SettingRow';
import { SettingsFieldRenderer } from './SettingsFieldRenderer';

interface ITagSettingsGroupProps {
	tagSettings: Api.ITagSettings;
	configuration: Api.IIntegrationConfiguration;
	provider: Api.IntegrationProvider;
	setTagsSettings: (settings: Api.ITagSettings) => void;
}

export const TagSettingsGroup: React.FC<ITagSettingsGroupProps> = ({
	tagSettings,
	configuration,
	provider,
	setTagsSettings,
}) => {
	const providerConfig = getProviderConfig(provider);
	const tagsSchema = providerConfig.tags;

	const onUpdate = (updatedSettings: Partial<Api.ITagSettings>) => {
		setTagsSettings({
			...tagSettings,
			...updatedSettings,
		});
	};

	// Helper to get enabled tags as array using provider config
	const getEnabledTags = (): string[] => {
		const enabledTags: string[] = [];
		if (!tagSettings) return enabledTags;

		// Check core tags if enabled for this provider
		if (tagsSchema?.showCoreTags) {
			CORE_TAGS.forEach(tag => {
				const value = getSettingValue<boolean>(tagSettings, tag.settingsKey);
				if (value) enabledTags.push(tag.id);
			});
		}

		// Check provider-specific tags from config
		if (tagsSchema?.providerTags) {
			tagsSchema.providerTags.forEach(tag => {
				const value = getSettingValue<boolean>(tagSettings, tag.settingsKey);
				if (value) enabledTags.push(tag.id);
			});
		}

		// Check dynamic tags from config (e.g., Salesforce enabledFields)
		if (tagsSchema?.dynamicTags) {
			const { settingsKey } = tagsSchema.dynamicTags;
			const dynamicTagIds = getSettingValue<string[]>(tagSettings, settingsKey) ?? [];
			enabledTags.push(...dynamicTagIds);
		}

		return enabledTags;
	};

	// Handle tag selection changes using provider config
	const handleTagChange = (selectedTags: string[]) => {
		const updatedSettings: Record<string, unknown> = {};

		// Update core tags if enabled for this provider
		if (tagsSchema?.showCoreTags) {
			CORE_TAGS.forEach(tag => {
				updatedSettings[tag.settingsKey] = selectedTags.includes(tag.id);
			});
		}

		// Update provider-specific tags from config
		if (tagsSchema?.providerTags) {
			tagsSchema.providerTags.forEach(tag => {
				updatedSettings[tag.settingsKey] = selectedTags.includes(tag.id);
			});
		}

		// Update dynamic tags from config (e.g., Salesforce enabledFields)
		if (tagsSchema?.dynamicTags) {
			const { optionsFrom, settingsKey } = tagsSchema.dynamicTags;
			const availableFieldKeys = Object.keys(getConfigValue<Record<string, string>>(configuration, optionsFrom) ?? {});
			const enabledFields = selectedTags.filter(tag => availableFieldKeys.includes(tag));
			updatedSettings[settingsKey] = enabledFields;
		}

		onUpdate(updatedSettings as Partial<Api.ITagSettings>);
	};

	// Get tag options based on provider config
	const getTagOptions = (): ComboboxItem[] => {
		const options: ComboboxItem[] = [];

		// Add core location tags if enabled by config
		if (tagsSchema?.showCoreTags) {
			options.push(...CORE_TAGS.map(t => ({ id: t.id, label: t.label })));
		}

		// Add provider-specific tags from config
		if (tagsSchema?.providerTags) {
			options.push(...tagsSchema.providerTags.map(t => ({ id: t.id, label: t.label })));
		}

		// Add dynamic tags from configuration (e.g., Salesforce availableFields)
		if (tagsSchema?.dynamicTags) {
			const { optionsFrom } = tagsSchema.dynamicTags;
			const dynamicOptions = getConfigValue<Record<string, string>>(configuration, optionsFrom);
			if (dynamicOptions) {
				options.push(...recordToItems(dynamicOptions));
			}
		}

		return options;
	};

	// Get display value for enabled tags
	const getTagOptionsDisplay = (): string => {
		const enabledTags = getEnabledTags();
		if (!enabledTags.length) return 'None selected';

		const tagOptions = getTagOptions();
		return enabledTags.map(tagId => tagOptions.find(opt => opt.id === tagId)?.label || tagId).join(', ');
	};

	// Render custom fields section if configured
	const renderCustomFields = () => {
		if (!tagsSchema?.customFields) return null;

		const { availableFrom, settingsKey } = tagsSchema.customFields;
		const availableFields = getAvailableFields(configuration, availableFrom);
		const enabledFields = getSettingValue<string[]>(tagSettings, settingsKey) ?? [];

		if (!availableFields || !Object.keys(availableFields).length) return null;

		return (
			<SettingRow title='Custom Fields' subtitle='Select custom data fields to be used as contact tags'>
				<EditModeCombobox
					multiple
					items={recordToItems(availableFields)}
					value={enabledFields}
					onChange={(selectedFields: string[]) => onUpdate({ [settingsKey]: selectedFields })}
					displayValue={getFieldsDisplayValue(enabledFields, availableFields)}
					searchPlaceholder='Search custom fields...'
				/>
			</SettingRow>
		);
	};

	// Get enabled values for a custom section based on its storage type
	const getCustomSectionEnabledValues = (section: CustomTagSection): string[] => {
		if (section.storageType === 'nested') {
			const nestedSettings = getSettingValue<Record<string, boolean>>(tagSettings, section.settingsKey);
			if (!nestedSettings) return [];
			return section.options.filter(opt => nestedSettings[opt.id]).map(opt => opt.label);
		} else {
			return getSettingValue<string[]>(tagSettings, section.settingsKey) ?? [];
		}
	};

	// Handle custom section value changes
	const handleCustomSectionChange = (section: CustomTagSection, selectedLabels: string[]) => {
		if (section.storageType === 'nested') {
			const nestedSettings: Record<string, boolean> = {};
			section.options.forEach(opt => {
				nestedSettings[opt.id] = selectedLabels.includes(opt.label);
			});
			onUpdate({ [section.settingsKey]: nestedSettings } as Partial<Api.ITagSettings>);
		} else {
			onUpdate({ [section.settingsKey]: selectedLabels } as Partial<Api.ITagSettings>);
		}
	};

	// Get display value for a custom section
	const getCustomSectionDisplayValue = (section: CustomTagSection): string => {
		const enabledValues = getCustomSectionEnabledValues(section);
		return enabledValues.length > 0 ? enabledValues.join(', ') : 'None selected';
	};

	// Render a custom tag section from config
	const renderCustomSection = (section: CustomTagSection) => {
		const enabledValues = getCustomSectionEnabledValues(section);

		return (
			<SettingRow key={section.id} title={section.label} subtitle={section.subtitle}>
				<EditModeMultipleSelect
					value={enabledValues}
					onChange={(selected: string[]) => handleCustomSectionChange(section, selected)}
					displayValue={getCustomSectionDisplayValue(section)}
				>
					<MultipleSelectTrigger styles={styleSheet.editableField}>
						<MultipleSelectValue values={enabledValues} placeholder={`Select ${section.label.toLowerCase()}`} />
					</MultipleSelectTrigger>
					<MultipleSelectContent>
						{section.options.map(opt => (
							<MultipleSelectItem key={opt.id} value={opt.label} selected={enabledValues.includes(opt.label)}>
								{opt.label}
							</MultipleSelectItem>
						))}
					</MultipleSelectContent>
				</EditModeMultipleSelect>
			</SettingRow>
		);
	};

	return (
		<SettingGroup title='Tag Settings'>
			<SettingRow title='Tags' subtitle='Select taggable information to associate with contacts'>
				<EditModeCombobox
					multiple
					items={getTagOptions()}
					value={getEnabledTags()}
					onChange={handleTagChange}
					displayValue={getTagOptionsDisplay()}
					searchPlaceholder='Search tags...'
				/>
			</SettingRow>

			{renderCustomFields()}
			{tagsSchema?.extraFields?.map(field => (
				<SettingsFieldRenderer
					key={field.key}
					field={field}
					settings={tagSettings}
					onUpdate={updates => onUpdate(updates as Partial<Api.ITagSettings>)}
					configuration={configuration}
				/>
			))}
			{tagsSchema?.customSections?.map(section => renderCustomSection(section))}
		</SettingGroup>
	);
};
